import { IIdentityVerificationField } from "@faire/web-api/indigofair/data/IIdentityVerificationField";
import { IIdentityVerificationRequest } from "@faire/web-api/indigofair/data/IIdentityVerificationRequest";
import { QueryParameters as ConfirmIdentityParameters } from "@faire/web-api/routes/confirm-identity";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { throttleIfPossible } from "@faire/web/common/globals/throttleIfPossible";
import { trackEvent } from "@faire/web/common/trackEvent";
import { TwoLetterStateCode } from "@faire/web/common/types/StateCodes";

import { STATES_WITHOUT_SALES_TAX } from "@faire/retailer/lib/getResellerCertificateFormData";
import { RetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { ConfirmIdentityState } from "@faire/retailer/views/confirm-identity/ConfirmIdentityState";

// Use the bell character to indicate that a field is being overwritten
export const OVERRIDE_CONSTRAINT_VALUE = "\x07";
export const CONFIRM_IDENTITY_RESELLER_INFO = "CONFIRM_IDENTITY_RESELLER_INFO";
export const CONFIRM_IDENTITY_RESALE_CERTIFICATE =
  "CONFIRM_IDENTITY_RESALE_CERTIFICATE";
export const CONFIRM_IDENTITY_GENERAL_INFO = "CONFIRM_IDENTITY_GENERAL_INFO";
export const CONFIRM_IDENTITY_STORE_IMAGES = "CONFIRM_IDENTITY_STORE_IMAGES";
export const CONFIRM_IDENTITY_GENERAL_INFO_OVERRIDE =
  "CONFIRM_IDENTITY_GENERAL_INFO_OVERRIDE";
export const CONFIRM_IDENTITY_CREDIT_STATEMENTS =
  "CONFIRM_IDENTITY_CREDIT_STATEMENTS";
export const CONFIRM_IDENTITY_BANK_INFO = "CONFIRM_IDENTITY_BANK_INFO";

export const CONFIRM_IDENTITY_QUERY_PARAM_NAMES = {
  EMAIL: "email",
};

export interface ConfirmIdentityQueryParams extends ConfirmIdentityParameters {
  plaidLinkToken: string | undefined;
  email: string | undefined;
  // TODO: Remove once web-api publishes https://github.com/Faire/backend/pull/150407
  state: string | undefined;
}
export namespace ConfirmIdentityQueryParams {
  export const parse = (search?: string) =>
    ConfirmIdentityParameters.parse(search) as ConfirmIdentityQueryParams;
}

export interface IdvUpfunnelState {
  isValid: () => boolean;
  noEmptyFields: boolean;
  noInvalidFields: boolean;
  formPayload: Partial<IIdentityVerificationRequest>;
  loadDraftIdv: () => void;
}

export const isAStepRequired = (
  stepFields: Array<keyof typeof IIdentityVerificationField.Type>,
  requestedFieldTypes: Array<keyof typeof IIdentityVerificationField.Type>
): boolean => {
  for (const field of stepFields) {
    if (requestedFieldTypes.includes(field)) {
      return true;
    }
  }
  return false;
};

export const scrollToTop = () => {
  try {
    getWindowOrThrow().scrollTo?.(0, 0);
  } catch (_err) {
    // silence is golden
  }
};

export const payloadFields = [
  "annual_sales_max_dollars",
  "annual_sales_min_dollars",
  "description",
  "open_date",
  "address",
  "ein",
  "vat",
  "facebook_url",
  "free_form_answer",
  "free_form_question",
  "reseller_id",
  "retailer_token",
  "store_images",
  "website_url",
  "year_established",
  "lease_time",
  "resale_certificates",
  "plaid_instrument_link",
  "bank_statements",
  "credit_card_statements",
  "lease_documents",
  "drivers_license",
  "ssn",
  "reseller_certificate",
  "idv_address",
  "proof_of_pop_up",
  "website_images",
  "abn",
  "eori",
  "dba_name_certificate",
  "store_pictures_with_signage",
  "tax_license_or_reseller_permit_proof",
];

export const autoIdvFieldsType = [
  "RESELLER_ID",
  "DESCRIPTION",
  "WEBSITE_URL",
  "FACEBOOK_URL",
  "STORE_IMAGES",
  "FREE_FORM_QUESTION",
  "RESALE_CERTIFICATES",
  "OPEN_DATE",
  "ADDRESS",
  "TAX_LICENSE_OR_RESELLER_PERMIT_PROOF",
];

export const trackConfirmIdentityStepMounted = (
  formState: ConfirmIdentityState
) => {
  trackEvent(
    `/confirm-identity/step-${formState.currentStepNumber}-of-${formState.numberOfSteps}-mounted`
  );
};

export const trackAskForResellerCertificateMounted = (
  step: string,
  source: string
) => {
  trackEvent(
    `/reseller-certificates/${source}/${step}-mounted/retailer/${
      RetailerStore.get().retailerToken
    }`
  );
};

export const storeFormData = throttleIfPossible(
  (storage, data) => storage.setData(data),
  50
);

export const isBasedInStateWithoutSalesTax = (
  basedInState: string | undefined,
  isBasedInUSA: boolean
) => {
  if (!isBasedInUSA) {
    return true;
  }
  return (
    !!basedInState &&
    STATES_WITHOUT_SALES_TAX.includes(basedInState as TwoLetterStateCode)
  );
};
